import type { ReactElement } from "react"
import { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useQuery } from "react-query"
import { useSelector } from "react-redux"
import { Link, useParams } from "react-router-dom"

import BubbleChartOutlinedIcon from "@mui/icons-material/BubbleChartOutlined"
import { CircularProgress, Grid } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { Button, DateRangePicker, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import type { MRT_PaginationState } from "material-react-table"

import { InfoContainer } from "../../components/core/InfoContainer"
import { SelectWithSearch } from "../../components/form/SelectWithSearch/SelectWithSearch"
import { RequestLogTable } from "../../components/tables/RequestLogTable"
import { KonanEmptyState } from "../../components/utility/KonanEmptyState"
import { useDateQuery } from "../../hooks/useDateQuery"
import { KonanAPI } from "../../services/KonanAPI"
import type { RootState } from "../../store/ReduxStore"
import type { ListModelOutputs } from "../../types/generated/api/ListModelOutputs"
import { Model } from "../../types/generated/api/Model"
import type { ModelListCreate } from "../../types/generated/api/ModelListCreate"
import type { PaginatedListModelOutputsList } from "../../types/generated/api/PaginatedListModelOutputsList"
import type { PaginatedModelListCreateList } from "../../types/generated/api/PaginatedModelListCreateList"
import { extractPageFromBackEndPaginationLink } from "../../utils/genericHelpers"
import { PredictionCreationFlow } from "../Predictions"
import { ComparisonTableContainer as ComparisonTable } from "./components/ComparisonTable"
import { ConfigurationAlert } from "./components/ConfigurationAlert"
import { DropDownMenu } from "./components/DropDownMenu"
import { EmptyState } from "./components/EmptyState"
import { RequestsPerDayGraph } from "./components/RequestsPerDayGraph"
import { TrainedByKonan } from "./components/TrainedByKonanTag"

/**
 * ModelsContainer component
 *
 * This component is responsible for displaying the models associated with a project.
 * It handles fetching model data, displaying loading states, and rendering various UI components
 * such as date pickers, logs, and tables for model requests and comparisons.
 *
 * @returns {ReactElement} The rendered component.
 */
export function ModelsContainer(): ReactElement {
  const { id: projectId } = useParams<{ id: string }>()

  const currentModel = useSelector((state: RootState) => state.models.currentModel)
  const { t } = useTranslation("models")

  // start and end date state for fetching api request data
  const [startDate, setStartDate, endDate, setEndDate] = useDateQuery()

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastItemRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isLoadingSearchResultModels && !isFetchingNextPage && fetchNextPage()
    },
  })

  // Pagination state for request logs table
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  // model that will be compared
  const [compareModel, setCompareModel] = useState<ModelListCreate | undefined>(undefined)
  const [modelsSearchValue, setModelsSearchValue] = useState<string | undefined>(undefined)
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState<boolean>(false)
  const [isTestModal, setIsTestModal] = useState(false)
  const [areNoModelsAvailable, setAreNoModelsAvailable] = useState<boolean>(false)

  const {
    data: searchResultModels,
    isLoading: isLoadingSearchResultModels,
    isError: isSearchResultModelsError,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedModelListCreateList>, AxiosError, ModelListCreate>(
    ["models", projectId, modelsSearchValue, "paginated", "state"],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchModels({
        projectId: projectId as string,
        page: pageParam,
        search: modelsSearchValue,
        ordering: "state",
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<ModelListCreate[]>(
          (accumulator, page) => accumulator.concat(page.data.results as ModelListCreate[]),
          [],
        ),
      }),
      onSuccess: (response) => {
        if (modelsSearchValue === undefined && response.pages.length === 0) {
          setAreNoModelsAvailable(true)
        } else {
          setAreNoModelsAvailable(false)
        }
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      refetchOnMount: true,
      enabled: !!projectId,
    },
  )

  const comparisonModelsOptions = [
    {
      text: t("comparison.select_none"),
      value: "none",
      item: (
        <Typography variant="a" variantColor={2}>
          {t("comparison.select_none")}
        </Typography>
      ),
      disabled: false,
    },
    ...(searchResultModels?.pages
      ?.filter((model) => model.uuid !== currentModel?.uuid)
      ?.map((model) => {
        return {
          text: model.name,
          value: model.uuid,
          item: <Typography variant="a">{model.name}</Typography>,
          disabled: false,
        }
      }) ?? []),
  ]

  /**
   * Fetch request logs for the current model
   * Includes pagination and date range filtering
   * Updates model A total count on successful response
   */
  const { isLoading: isLoadingRequestLogTable, data: requestLogTableResponse } = useQuery<
    AxiosResponse<PaginatedListModelOutputsList>
  >(
    ["request-log", startDate, endDate, projectId, currentModel, pagination.pageSize, pagination.pageIndex],
    () =>
      KonanAPI.fetchModelRequestLogData(
        startDate?.toISOString() as string,
        endDate?.toISOString() as string,
        currentModel?.uuid as string,
        pagination.pageIndex + 1,
        pagination.pageSize,
      ),
    {
      keepPreviousData: true,
      enabled: currentModel?.uuid !== undefined,
    },
  )

  /**
   * Handles selection change in model comparison dropdown
   *
   * @param {string} item - The selected model ID and name or "none".
   */
  const handleComparisonModelChange = (item: { value: string; text: string }): void => {
    if (item.value === "none") {
      setCompareModel(undefined)
    } else {
      setCompareModel(searchResultModels?.pages.find((model) => model.uuid === item.value))
    }
  }

  const isCurrentModelMissing = !currentModel
  const hasErrorOccurred =
    !currentModel || areNoModelsAvailable || (modelsSearchValue === undefined && isSearchResultModelsError)

  // If models are still loading or currentModel is missing or has not returned from context
  if (
    ((modelsSearchValue === undefined && isLoadingSearchResultModels) || isCurrentModelMissing) &&
    !areNoModelsAvailable
  )
    return (
      <Grid container xs={12} className={"empty-container"} width={"100%"} height={"100%"}>
        <InfoContainer icon={<CircularProgress />} title={t("loading_state.title")} />
      </Grid>
    )

  // If no models are available
  if (areNoModelsAvailable) return <EmptyState />

  // If an error has occurred
  if (hasErrorOccurred)
    return (
      <Grid container xs={12} className={"empty-container"} width={"100%"} height={"100%"}>
        <KonanEmptyState title={t("error_state.title")} subTitle={t("error_state.subtitle")} />
      </Grid>
    )

  return (
    <Fragment>
      {/* checks if a non generic project is not configured to display a config alert */}
      {/* configuration button does not work since configuration screen is not present */}
      <ConfigurationAlert />

      <PredictionCreationFlow
        isTestModal={isTestModal}
        isOpen={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
      />

      <Grid container gap={2}>
        <Grid container display={"flex"} gap={2} justifyContent={"space-between"}>
          <Grid container item gap={1} width={"fit-content"} alignItems={"center"}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              disableFuture
            />

            <TrainedByKonan />
          </Grid>

          <Grid item display={"flex"} gap={1} justifyContent={"center"} alignItems={"center"} direction={"row"}>
            {currentModel?.state === Model.state.LIVE && (
              <Button
                variant="primary"
                onClick={() => {
                  setIsTestModal(false)
                  setIsUploadDialogOpen(true)
                }}
                key={"upload_btn"}
              >
                {t("header.buttons.predict")}
              </Button>
            )}

            <Button
              variant="secondary"
              onClick={() => {
                setIsTestModal(true)
                setIsUploadDialogOpen(true)
              }}
            >
              {t("header.buttons.Test")}
            </Button>

            <Link to={`/projects/${projectId}/models/classification`} style={{ textDecoration: "none" }}>
              <Button variant="secondary" startIcon={<BubbleChartOutlinedIcon fontSize="small" />} size="regular">
                {t("header.buttons.classification_metrics")}
              </Button>
            </Link>

            <DropDownMenu model={currentModel} />
          </Grid>
        </Grid>

        <Grid container columnSpacing={{ xs: 0, lg: 2 }} rowSpacing={{ xs: 2, lg: 0 }}>
          {/* Requests Line Graph */}
          <Grid item xs={12}>
            <RequestsPerDayGraph startDate={startDate} endDate={endDate} />
          </Grid>
        </Grid>

        {/* hide menu if there is only one model */}
        {!areNoModelsAvailable && (
          <Grid item xs={12} md={4} lg={3}>
            <SelectWithSearch
              optionsList={comparisonModelsOptions}
              isOptionsLoading={isLoadingSearchResultModels || isFetchingNextPage}
              inputValue={compareModel?.name ?? t("comparison.select_model")}
              menuTriggerProps={{
                placeholder: t("comparison.select_model"),
                label: t("comparison.compare_with"),
                fullWidth: true,
                hideDescription: true,
              }}
              onSelectMenuItem={({ value, text }) => handleComparisonModelChange({ value, text })}
              searchInputChangeHandler={(e: InputChangeEvent) => {
                setModelsSearchValue(e.target.value.toString())
              }}
              onMenuClose={() => setModelsSearchValue(undefined)}
              lastMenuItemRef={lastItemRef}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          {/*
            Requests log table
            Will display model request logs since modelId is supplied.
          */}
          {!compareModel ? (
            <RequestLogTable
              modelId={currentModel?.uuid}
              data={requestLogTableResponse?.data?.results as Array<ListModelOutputs>}
              rowCount={requestLogTableResponse?.data?.count}
              isFetching={isLoadingRequestLogTable}
              pagination={pagination}
              setPagination={setPagination}
              modelName={currentModel?.name}
            />
          ) : (
            /* Model comparison log table */
            <ComparisonTable
              modelA={{
                response: requestLogTableResponse?.data?.results as Array<ListModelOutputs>,
                isFetching: isLoadingRequestLogTable,
                totalCount: requestLogTableResponse?.data?.count ?? 0,
              }}
              compareModel={compareModel}
            />
          )}
        </Grid>
      </Grid>
    </Fragment>
  )
}
